import { defineAsyncComponent } from '../../../lib/utils/vue3compat/vue.js?vue3';
import { GlAlert } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import Autosize from 'autosize';
import { __ } from '~/locale';
import axios from '../../../lib/utils/axios_utils.js?vue3';
import { updateDraft, clearDraft, getDraft } from '~/lib/utils/autosave';
import { setUrlParams, joinPaths } from '~/lib/utils/url_utility';
import LocalStorageSync from '../local_storage_sync.vue?vue3';
import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
import { EDITING_MODE_KEY, EDITING_MODE_MARKDOWN_FIELD, EDITING_MODE_CONTENT_EDITOR, EDITING_MODE_EVENTS, CLEAR_AUTOSAVE_ENTRY_EVENT } from '../../constants';
import { getInitialEditingMode } from './utils';
import MarkdownField from './field.vue?vue3';
import eventHub from './eventhub';
export default {
    name: 'MarkdownEditor',
    components: {
        GlAlert,
        MarkdownField,
        LocalStorageSync,
        ContentEditor: defineAsyncComponent(()=>import(/* webpackChunkName: 'content_editor' */ '../../../content_editor/components/content_editor.vue?vue3'))
    },
    mixins: [
        glSlotsMixin
    ],
    props: {
        value: {
            type: String,
            required: true
        },
        setFacade: {
            type: Function,
            required: false,
            default: null
        },
        renderMarkdownPath: {
            type: String,
            required: true
        },
        uploadsPath: {
            type: String,
            required: false,
            default: ()=>window.uploads_path || ''
        },
        enableContentEditor: {
            type: Boolean,
            required: false,
            default: true
        },
        formFieldProps: {
            type: Object,
            required: true
        },
        autofocus: {
            type: Boolean,
            required: false,
            default: false
        },
        enableAutocomplete: {
            type: Boolean,
            required: false,
            default: true
        },
        autocompleteDataSources: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        supportsQuickActions: {
            type: Boolean,
            required: false,
            default: false
        },
        supportsTableOfContents: {
            type: Boolean,
            required: false,
            default: false
        },
        autosaveKey: {
            type: String,
            required: false,
            default: null
        },
        markdownDocsPath: {
            type: String,
            required: false,
            default: ''
        },
        drawioEnabled: {
            type: Boolean,
            required: false,
            default: false
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        },
        disableAttachments: {
            type: Boolean,
            required: false,
            default: false
        },
        codeSuggestionsConfig: {
            type: Object,
            required: false,
            default: ()=>({
                    lines: [],
                    lineType: '',
                    canSuggest: false,
                    showPopover: false
                })
        },
        noteableType: {
            type: String,
            required: false,
            default: ''
        },
        restrictedToolBarItems: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        restoreFromAutosave: {
            type: Boolean,
            required: false,
            default: false
        },
        newCommentTemplatePaths: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        editorAiActions: {
            type: Array,
            required: false,
            default: ()=>[]
        },
        immersive: {
            type: Boolean,
            required: false,
            default: false
        },
        fillContainer: {
            type: Boolean,
            required: false,
            default: false
        },
        hideModeSwitcher: {
            type: Boolean,
            required: false,
            default: false
        },
        collaborationProvider: {
            type: Object,
            required: false,
            default: null
        },
        beforeContentEditor: {
            type: Function,
            required: false,
            default: null
        }
    },
    emits: [
        'blur',
        'content-editor',
        'focus',
        'handle-suggest-dismissed',
        'input',
        'keydown',
        'markdown-field'
    ],
    data () {
        const editingMode = getInitialEditingMode();
        const autosaveValue = this.autosaveKey ? getDraft(this.autosaveKey) : '';
        const initialValue = this.restoreFromAutosave ? autosaveValue : this.value;
        return {
            alert: null,
            markdown: initialValue || autosaveValue || '',
            editingMode,
            autofocused: false,
            contentEditorFocusPending: false
        };
    },
    computed: {
        isContentEditorActive () {
            return this.enableContentEditor && this.editingMode === EDITING_MODE_CONTENT_EDITOR;
        },
        contentEditorAutofocused () {
            // Match textarea focus behavior
            return this.autofocus && !this.autofocused ? 'end' : false;
        },
        markdownFieldRestrictedToolBarItems () {
            const restrictAttachments = this.disableAttachments ? [
                'attach-file'
            ] : [];
            return [
                ...this.restrictedToolBarItems,
                ...restrictAttachments
            ];
        },
        isDefaultEditorEnabled () {
            return [
                'plain_text_editor',
                'rich_text_editor'
            ].includes(window.gon?.text_editor);
        }
    },
    watch: {
        value: 'updateValue',
        collaborationProvider (provider) {
            if (provider) this.alert = null;
        }
    },
    mounted () {
        this.autofocusTextarea();
        // Second argument (`true`) is passed to identify
        // that the input event was emitted on component mount.
        this.$emit('input', this.markdown, true);
        this.$emit(EDITING_MODE_EVENTS[this.editingMode]);
        this.saveDraft();
        this.setFacade?.({
            getValue: ()=>this.getValue(),
            setValue: (val)=>this.setValue(val)
        });
        eventHub.$on(CLEAR_AUTOSAVE_ENTRY_EVENT, this.clearDraft);
    },
    beforeDestroy () {
        eventHub.$off(CLEAR_AUTOSAVE_ENTRY_EVENT, this.clearDraft);
    },
    methods: {
        getValue () {
            return this.markdown;
        },
        setValue (value) {
            this.$emit('input', value);
            this.updateValue(value);
        },
        updateValue (value) {
            this.markdown = value;
            this.saveDraft();
            this.autosizeTextarea();
        },
        // eslint-disable-next-line vue/no-unused-properties -- append() is part of the component's public API.
        append (value) {
            if (!value) {
                this.focus();
                return;
            }
            const newValue = [
                this.markdown.trim(),
                value
            ].filter(Boolean).join('\n\n');
            this.updateValue(`${newValue}\n\n`);
            this.$nextTick(()=>{
                this.focus();
            });
        },
        // eslint-disable-next-line vue/no-unused-properties -- setTemplate() is part of the component's public API.
        setTemplate (template, force = false) {
            if (!this.markdown || force) {
                this.setValue(template);
            } else {
                const dismiss = ()=>{
                    this.alert = null;
                };
                this.alert = {
                    message: this.$options.i18n.applyTemplateAlert.message,
                    variant: 'warning',
                    primaryButtonText: this.$options.i18n.applyTemplateAlert.primaryButtonText,
                    secondaryButtonText: this.$options.i18n.applyTemplateAlert.secondaryButtonText,
                    primaryAction: ()=>{
                        this.setValue(template, true);
                        dismiss();
                    },
                    secondaryAction: dismiss,
                    dismiss
                };
            }
        },
        updateMarkdownFromContentEditor ({ markdown }) {
            this.markdown = markdown;
            this.$emit('input', markdown);
            this.saveDraft();
        },
        updateMarkdownFromMarkdownField ({ target }) {
            this.markdown = target.value;
            this.$emit('input', target.value);
            this.saveDraft();
        },
        renderMarkdown (markdown) {
            const url = setUrlParams({
                render_quick_actions: this.supportsQuickActions,
                no_header_anchors: !this.supportsTableOfContents
            }, {
                url: joinPaths(window.location.origin, this.renderMarkdownPath)
            });
            return axios.post(url, {
                text: markdown
            }).then(({ data: { html, body, ...otherData } = {} } = {})=>({
                    body: body || html || '',
                    ...otherData
                }));
        },
        async onEditingModeChange (editingMode) {
            if (editingMode === EDITING_MODE_CONTENT_EDITOR && this.beforeContentEditor && !await this.beforeContentEditor(this.markdown)) {
                return;
            }
            this.editingMode = editingMode;
            this.notifyEditingModeChange(editingMode);
        },
        onEditingModeRestored (editingMode) {
            if (!this.isDefaultEditorEnabled) return;
            if (editingMode === EDITING_MODE_CONTENT_EDITOR && !this.enableContentEditor) {
                this.editingMode = EDITING_MODE_MARKDOWN_FIELD;
                return;
            }
            this.editingMode = editingMode;
            this.$emit(EDITING_MODE_EVENTS[editingMode]);
            this.notifyEditingModeChange(editingMode);
        },
        async notifyEditingModeChange (editingMode) {
            this.$emit(EDITING_MODE_EVENTS[editingMode]);
            this.contentEditorFocusPending = editingMode === EDITING_MODE_CONTENT_EDITOR;
            if (this.contentEditorFocusPending) return;
            await this.$nextTick();
            this.$refs.textarea.focus();
        },
        onContentEditorInitialized () {
            this.setEditorAsAutofocused();
            if (!this.contentEditorFocusPending) return;
            this.contentEditorFocusPending = false;
            this.$refs.contentEditor.focus();
        },
        autofocusTextarea () {
            if (this.autofocus && this.editingMode === EDITING_MODE_MARKDOWN_FIELD) {
                this.$refs.textarea.focus();
                this.setEditorAsAutofocused();
            }
        },
        focus () {
            if (this.editingMode === EDITING_MODE_MARKDOWN_FIELD) {
                this.$refs.textarea.focus();
            } else {
                this.$refs.contentEditor.focus();
            }
        },
        setEditorAsAutofocused () {
            this.autofocused = true;
        },
        saveDraft () {
            if (!this.autosaveKey) return;
            if (this.markdown) updateDraft(this.autosaveKey, this.markdown);
            else clearDraft(this.autosaveKey);
        },
        clearDraft (key) {
            if (!this.autosaveKey || key !== this.autosaveKey) return;
            clearDraft(this.autosaveKey);
        },
        // eslint-disable-next-line vue/no-unused-properties -- togglePreview() is part of the component's public API.
        togglePreview (value) {
            if (this.editingMode === EDITING_MODE_MARKDOWN_FIELD) {
                this.$refs.markdownField.previewMarkdown = value;
            }
        },
        autosizeTextarea () {
            if (this.editingMode === EDITING_MODE_MARKDOWN_FIELD) {
                this.$nextTick(()=>{
                    Autosize.update(this.$refs.textarea);
                });
            }
        },
        onKeydown (event) {
            const isModifierKey = event.ctrlKey || event.metaKey;
            if (isModifierKey && event.key === 'k') {
                event.preventDefault();
            }
            this.$emit('keydown', event);
        }
    },
    EDITING_MODE_KEY,
    i18n: {
        applyTemplateAlert: {
            message: __('Applying a template will replace the existing content. Any changes you have made will be lost.'),
            primaryButtonText: __('Apply template'),
            secondaryButtonText: __('Cancel')
        }
    }
};
